import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { NAMESPACE } from '@/constants';
import { formatCompactMoney, formatMoney } from '@/constants/currency';
import { useShellPalette } from '@/styles';
import type { Zoom } from './useTimelineModel';
import {
  ChartRow,
  Panel,
  PanelFact,
  PanelFacts,
  PanelHead,
  PanelHint,
  PanelScroll,
  PanelTitle,
  PlotCanvas,
  TooltipCard,
  TooltipRow,
  TooltipTitle
} from './CampaignTimeline.styles';

interface VolumePoint {
  key: string;
  label: string;
  live: number;
  net: number;
}

interface MixSlice {
  name: string;
  value: number;
  count: number;
  colour: string;
}

interface Props {
  volume: VolumePoint[];
  channelMix: MixSlice[];
  zoom: Zoom;
  activeChannel: string | null;
  onChannelSelect: (channel: string) => void;
}

/**
 * Plot heights for the three timeline panels.
 *
 * Kept together because the panels are grid siblings stretching to a shared
 * height: whichever plot is tallest sets the height of all three cards, so a
 * generous channel-mix height silently makes the other two cards taller than
 * their own plots need. Changing one in isolation is what produces cards with
 * dead space in them.
 *
 * Sized so the band stays context for the table beneath rather than pushing it
 * off screen — these charts are read at a glance, not studied.
 */
const CHART = {
  flightsHeight: 132,
  netHeight: 132,
  mixRowHeight: 20,
  /* Floor so a book of business with two channels still reads as a chart
     rather than a pair of stray bars, and a ceiling so eight channels cannot
     stretch the whole band. */
  mixMinHeight: 132,
  mixMaxHeight: 160
} as const;

/** The concurrency series is a single measure, so it takes categorical slot 1
 *  and needs no legend -- the panel title names it. */
const SERIES = '#3987e5';
/** Net is a different measure on its own panel, so it takes its own slot
 *  rather than sharing the blue and implying the two are one series. */
const MONEY_SERIES = '#199e70';

/**
 * Two overview panels above the Gantt.
 *
 * Both answer a question the bars cannot: how crowded the market is over time,
 * and where the money sits by channel. Each is a single measure on a single
 * axis -- flights in market and net are never plotted against each other on
 * one plot, because the alignment of two such scales would be arbitrary.
 */
export const TimelineCharts: FC<Props> = ({
  volume,
  channelMix,
  zoom,
  activeChannel,
  onChannelSelect
}) => {
  const { t } = useTranslation(NAMESPACE.BOOKING_SHEET_PAGE);
  /* Resolved colours, not CSS variables: Recharts writes these into SVG
     attributes, where var() is not reliably supported. */
  const shell = useShellPalette();
  const AXIS = shell.textMuted;
  const GRID = shell.chartGrid;
  /** Reference marks -- an average line and the peak -- are annotation, not
   *  data, so they sit in a neutral ink rather than taking a categorical hue. */
  const REFERENCE = shell.chartReference;

  /* The plot needs a pixel width, so the panel's own width has to be measured
     rather than inherited through a percentage. */
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState(600);

  useEffect(() => {
    const node = panelRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      /* contentRect already excludes padding, and a pixel is shaved so a plot
         that exactly fits does not leave a scrollbar permanently showing. */
      setPanelWidth(Math.max(Math.floor(entry.contentRect.width) - 1, 240));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!volume.length && !channelMix.length) return null;

  const asBars = volume.length <= 12;

  /* Each bucket keeps a readable slice of width; past that the plot scrolls
     rather than compressing. Squeezed to the panel, a four-year weekly range
     drew ~200 points as one flat line with colliding tick labels. */
  /* A month label ("May 2023") is ~52px, so the buckets have to clear that or
     the axis prints them touching. Weeks use a shorter label ("6 Jul"). */
  const minBucketWidth = asBars ? 64 : zoom === 'week' ? 44 : 58;
  /* Measured from the panel so a short range fills it rather than huddling at
     the left edge, and a long one overflows into the scroller. */
  const plotWidth = Math.max(volume.length * minBucketWidth, panelWidth);

  /* The mix chart runs horizontally: its rows need height, not width. One
     comfortable band per channel, so eight channels are not squeezed into the
     same space that three would use. */
  const mixRows = channelMix.slice(0, 8);
  /* Matches the time-series panels' combined plot height so the three end
     level, rather than leaving dead space under the last channel bar. */
  const mixHeight = Math.min(
    Math.max(mixRows.length * CHART.mixRowHeight + 24, CHART.mixMinHeight),
    CHART.mixMaxHeight
  );

  /* Reference marks give the curve something to be read against: without them
     "90 flights" in a peak month is a number with no sense of whether that is
     busy or quiet for this book of business. */
  const counts = volume.map((point) => point.live);
  const average = counts.length
    ? counts.reduce((total, n) => total + n, 0) / counts.length
    : 0;
  const peak = volume.reduce<VolumePoint | null>(
    (best, point) => (!best || point.live > best.live ? point : best),
    null
  );
  const totalNet = volume.reduce((total, point) => total + point.net, 0);

  return (
    <ChartRow>
      <Panel ref={panelRef}>
        <PanelHead>
          <div>
            <PanelTitle>{t('timelineChartVolumeTitle')}</PanelTitle>
            <PanelHint>
              {zoom === 'week' ? t('timelineChartVolumeWeek') : t('timelineChartVolumeMonth')}
            </PanelHint>
          </div>
          {/* The two figures worth reading off this chart, stated rather than
              left to be estimated from the curve. */}
          <PanelFacts>
            {peak && (
              <PanelFact>
                <span>{t('timelineChartPeak')}</span>
                <strong>
                  {peak.live} · {peak.label}
                </strong>
              </PanelFact>
            )}
            <PanelFact>
              <span>{t('timelineChartAverage')}</span>
              <strong>{Math.round(average)}</strong>
            </PanelFact>
          </PanelFacts>
        </PanelHead>
        <PanelScroll>
        {/* An explicit width, not ResponsiveContainer's "100%": inside a
            horizontal scroller the percentage resolves against the visible
            panel, so 47 monthly buckets were drawn into one screen width with
            every tick label overlapping. */}
        <PlotCanvas $minWidth={plotWidth}>
        <ResponsiveContainer width={plotWidth} height={CHART.flightsHeight}>
          {/* Few buckets are discrete periods, not a trend: three months drawn
              as an area read as one flat line. Past a dozen the shape of the
              curve is the point, so it becomes an area. */}
          <ComposedChart data={volume} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
            <defs>
              <linearGradient id="timelineVolumeFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={SERIES} stopOpacity={0.45} />
                <stop offset="100%" stopColor={SERIES} stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={GRID} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: AXIS, fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: GRID }}
              interval={0}
              minTickGap={4}
            />
            <YAxis
              tick={{ fill: AXIS, fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={44}
              allowDecimals={false}
            />
            <Tooltip
              cursor={
                asBars
                  ? { fill: shell.menuHoverBg }
                  : { stroke: shell.accent, strokeWidth: 1 }
              }
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const point = payload[0].payload as VolumePoint;
                return (
                  <TooltipCard>
                    <TooltipTitle>{point.label}</TooltipTitle>
                    <TooltipRow>
                      {t('timelineChartInMarket')} <strong>{point.live}</strong>
                    </TooltipRow>
                    <TooltipRow>
                      {t('timelineChartNetInPeriod')}{' '}
                      <strong>{formatCompactMoney(point.net)}</strong>
                    </TooltipRow>
                  </TooltipCard>
                );
              }}
            />
            {/* The mean, so a bucket reads as busy or quiet rather than just
                as a number. Dashed because it is an annotation, not data. */}
            {volume.length > 2 && (
              <ReferenceLine
                y={average}
                stroke={REFERENCE}
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
              />
            )}
            {asBars ? (
              <Bar
                dataKey="live"
                fill={SERIES}
                radius={[4, 4, 0, 0]}
                maxBarSize={72}
                isAnimationActive={false}
              />
            ) : (
              <Area
                type="monotone"
                dataKey="live"
                stroke={SERIES}
                strokeWidth={2}
                fill="url(#timelineVolumeFill)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: shell.panelBg }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        </PlotCanvas>
        </PanelScroll>
      </Panel>

      {/* Net gets its own panel rather than a second y-axis on the chart
          above: two measures of different scale share no meaningful zero, and
          aligning them would invent a correlation that is not in the data. */}
      <Panel>
        <PanelHead>
          <div>
            <PanelTitle>{t('timelineChartNetTitle')}</PanelTitle>
            <PanelHint>{t('timelineChartNetHint')}</PanelHint>
          </div>
          <PanelFacts>
            <PanelFact>
              <span>{t('timelineChartTotal')}</span>
              <strong>{formatCompactMoney(totalNet)}</strong>
            </PanelFact>
          </PanelFacts>
        </PanelHead>
        <PanelScroll>
          <PlotCanvas $minWidth={plotWidth}>
            <ResponsiveContainer width={plotWidth} height={CHART.netHeight}>
              <ComposedChart data={volume} margin={{ top: 4, right: 8, bottom: 0, left: -4 }}>
                <defs>
                  <linearGradient id="timelineNetFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={MONEY_SERIES} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={MONEY_SERIES} stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: AXIS, fontSize: 10 }}
                  tickLine={false}
                  axisLine={{ stroke: GRID }}
                  interval={0}
                  minTickGap={4}
                />
                <YAxis
                  tick={{ fill: AXIS, fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={(value: number) => formatCompactMoney(value)}
                />
                <Tooltip
                  cursor={
                    asBars
                      ? { fill: shell.menuHoverBg }
                      : { stroke: shell.accent, strokeWidth: 1 }
                  }
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const point = payload[0].payload as VolumePoint;
                    return (
                      <TooltipCard>
                        <TooltipTitle>{point.label}</TooltipTitle>
                        <TooltipRow>
                          {t('timelineChartNetInPeriod')}{' '}
                          <strong>{formatMoney(point.net, 'GBP')}</strong>
                        </TooltipRow>
                        <TooltipRow>
                          {t('timelineChartInMarket')} <strong>{point.live}</strong>
                        </TooltipRow>
                      </TooltipCard>
                    );
                  }}
                />
                {asBars ? (
                  <Bar
                    dataKey="net"
                    fill={MONEY_SERIES}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={72}
                    isAnimationActive={false}
                  />
                ) : (
                  <Area
                    type="monotone"
                    dataKey="net"
                    stroke={MONEY_SERIES}
                    strokeWidth={2}
                    fill="url(#timelineNetFill)"
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2, stroke: shell.panelBg }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </PlotCanvas>
        </PanelScroll>
      </Panel>

      <Panel>
        <PanelTitle>{t('timelineChartMixTitle')}</PanelTitle>
        <PanelHint>{t('timelineChartMixHint')}</PanelHint>
        <ResponsiveContainer width="100%" height={mixHeight}>
          <BarChart
            data={mixRows}
            layout="vertical"
            margin={{ top: 4, right: 12, bottom: 0, left: 4 }}
            barCategoryGap={6}
          >
            <CartesianGrid stroke={GRID} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fill: AXIS, fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => formatCompactMoney(value)}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: AXIS, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              /* Wide enough for the longest real channel name ("Data - Direct")
                 on one line: wrapped, it collided with its neighbour's row. */
              width={104}
              /* Every channel is labelled, not every other one -- recharts
                 drops ticks it thinks will collide, which silently hid the
                 smallest channels' names. */
              interval={0}
            />
            <Tooltip
              cursor={{ fill: shell.menuHoverBg }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const slice = payload[0].payload as MixSlice;
                return (
                  <TooltipCard>
                    <TooltipTitle>{slice.name}</TooltipTitle>
                    <TooltipRow>
                      {t('net')} <strong>{formatMoney(slice.value, 'GBP')}</strong>
                    </TooltipRow>
                    <TooltipRow>
                      {t('timelineChartFlights')} <strong>{slice.count}</strong>
                    </TooltipRow>
                  </TooltipCard>
                );
              }}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" isAnimationActive={false}>
              {mixRows.map((slice) => (
                <Cell
                  key={slice.name}
                  fill={slice.colour}
                  /* The selected channel keeps full strength and the rest step
                     back, so the filter state is visible in the chart itself. */
                  fillOpacity={!activeChannel || activeChannel === slice.name ? 1 : 0.35}
                  onClick={() => onChannelSelect(slice.name)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </ChartRow>
  );
};
